import { useState, useRef } from "react";
import { makeStyles } from "@material-ui/styles";
import { Helmet } from "react-helmet";
import RunHistory from "./RunHistory";
import WorkbenchForm from "./WorkbenchForm";
import { colors } from "../../theme/variables";
import { useStartWorkflow } from "../../data/workflow";
import ExecutionHistory from "./ExecutionHistory";
import DefaultLoader from "../../components/DefaultLoader";
import { useTabsList } from "../../contexts/TabsContext";

const useStyles = makeStyles({
  wrapper: {
    height: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    position: "relative",
    backgroundColor: "#f8f9fa",
  },
  name: {
    width: "50%",
  },
  submitButton: {
    float: "right",
  },
  toolbar: {
    backgroundColor: colors.gray14,
  },
  workflowName: {
    fontWeight: "bold",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    margin: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  row: {
    display: "flex",
    flexDirection: "row",
  },
  fields: {
    margin: 30,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 15,
  },
  runInfo: {
    marginLeft: -350,
  },
  sidePanel: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    margin: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  noAccessMessage: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
    fontSize: "16px",
    color: "#666",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    margin: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
});

export default function Workbench() {
  const { tabsList } = useTabsList();

  const WorkbenchAccess = tabsList?.Workbench || {
    active: "True",
    hide: "False",
    run_history: {
      active: "True",
      hide: "False",
    },
    workflow_workbench: {
      active: "True",
      hide: "False",
    },
    execution_history: {
      active: "True",
      hide: "False",
    },
  };

  // Check if main workbench should be accessible
  const isWorkbenchAccessible =
    WorkbenchAccess.active === "True" && WorkbenchAccess.hide === "False";

  // Check individual component permissions
  const isRunHistoryAccessible =
    WorkbenchAccess.run_history?.active === "True" &&
    WorkbenchAccess.run_history?.hide === "False";
  const isWorkflowWorkbenchAccessible =
    WorkbenchAccess.workflow_workbench?.active === "True" &&
    WorkbenchAccess.workflow_workbench?.hide === "False";
  const isExecutionHistoryAccessible =
    WorkbenchAccess.execution_history?.active === "True" &&
    WorkbenchAccess.execution_history?.hide === "False";

  const classes = useStyles();

  const runHistoryRef = useRef();
  const [run, setRun] = useState(undefined);
  const [loading, isLoading] = useState(false);

  const { mutate: startWorkflow } = useStartWorkflow({
    onSuccess: (workflowId, variables) => {
      runHistoryRef.current.updateRun(variables.createTime, workflowId);
    },
  });

  const handleRunSelect = (run) => {
    setRun(run);
  };

  const setLoading = () => {
    isLoading(true);
  };
  const resetLoading = () => {
    isLoading(false);
  };

  const handleSaveRun = (runPayload) => {
    const newRun = runHistoryRef.current.pushNewRun(runPayload);
    setRun(newRun);
    return newRun;
  };

  const handleExecuteRun = (createTime, runPayload) => {
    startWorkflow({
      createTime,
      body: runPayload,
    });
  };

  // Return early if user doesn't have access to workbench
  if (!isWorkbenchAccessible) {
    return null;
  }

  // Check if user has access to any workbench tabs
  const hasAccessToAnyTab =
    isRunHistoryAccessible ||
    isWorkflowWorkbenchAccessible ||
    isExecutionHistoryAccessible;

  return (
    <>
      <Helmet>
        <title>Conductor UI - Workbench</title>
      </Helmet>

      <DefaultLoader displayModal={loading} />
      <div className={classes.wrapper}>
        {!hasAccessToAnyTab ? (
          <div className={classes.noAccessMessage}>
            You do not have access to any Workbench tabs.
          </div>
        ) : (
          <>
            {isRunHistoryAccessible && (
              <div className={classes.sidePanel}>
                <RunHistory
                  ref={runHistoryRef}
                  onRunSelected={handleRunSelect}
                />
              </div>
            )}

            {isWorkflowWorkbenchAccessible && (
              <div className={classes.main}>
                <WorkbenchForm
                  selectedRun={run}
                  saveRun={handleSaveRun}
                  executeRun={handleExecuteRun}
                />
              </div>
            )}

            {isExecutionHistoryAccessible && (
              <div className={classes.sidePanel}>
                <ExecutionHistory
                  run={run}
                  setLoading={setLoading}
                  resetLoading={resetLoading}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
